import moment from 'moment';
import { CSSProperties, Fragment } from 'react';
import { Center, Column, Icon, LineClamp, Row, ScrollArea, SortingArrow } from '~/commonComponents';
import browserHistory from '~/history';
import { Colors } from '~/themes/colors';
import { SortFields } from './enums';
import { useAllTransactionReceiptV2 } from './hooks';
import { ReceiptStatus } from './Status';

interface IProps {
  filters: {
    transactionDate: string;
  };
}

export const ReceiptTableV2 = ({ filters }: IProps) => {
  const { receipts, scrollContainerRef, sort, onFetchNextPage, onSortChange } =
    useAllTransactionReceiptV2({ filters });

  return (
    <ScrollArea ref={scrollContainerRef} onEndReached={onFetchNextPage}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }} data-cy="all-transactions-table">
        <thead
          data-cy="payment-history-table-header"
          style={{
            position: 'sticky',
            top: 0,
            backgroundColor: Colors.white,
            zIndex: 1,
          }}
        >
          <tr>
            <th
              style={{
                ...Styles.tableHeader,
                width: 240,
              }}
            >
              Office
            </th>
            <th
              role="presentation"
              style={{
                ...Styles.tableHeader,
                cursor: 'pointer',
              }}
              onClick={() => onSortChange(SortFields.TransactionDate)}
            >
              <Row gap={4} justifyContent="center" width="100%">
                <Column>
                  <span>Transaction</span>
                  <span>Date / Time</span>
                </Column>
                <SortingArrow
                  sorting={{
                    direction: sort.order,
                    field: sort.field,
                  }}
                  name={SortFields.TransactionDate}
                />
              </Row>
            </th>
            <th style={Styles.tableHeader}>Total Charge</th>
            <th style={Styles.tableHeader}>Total Refund</th>
            <th
              role="presentation"
              style={{
                ...Styles.tableHeader,
                cursor: 'pointer',
              }}
              onClick={() => onSortChange(SortFields.ShiftDate)}
            >
              <Row gap={4} justifyContent="center" width="100%">
                <span>Shift Date</span>
                <SortingArrow
                  sorting={{
                    direction: sort.order,
                    field: sort.field,
                  }}
                  name={SortFields.ShiftDate}
                />
              </Row>
            </th>
            <th style={Styles.tableHeader}>Status</th>
            <th style={Styles.tableHeader}>View Shift</th>
          </tr>
        </thead>
        <tbody>
          {receipts.map((it) => (
            <Fragment key={it.officeId}>
              <tr>
                <td colSpan={7} height={10} />
              </tr>
              <tr
                data-cy="payment-history-table-row"
                role="presentation"
                style={{
                  backgroundColor: Colors.neutral_50,
                  cursor: 'pointer',
                  height: 60,
                }}
                onClick={() => {
                  browserHistory.push(`job/${it.shiftId}/details`, {
                    job_id: it.shiftId,
                    jobId: it.shiftId,
                  });
                }}
              >
                <td
                  data-cy="office-name-column"
                  style={{
                    ...Styles.tableBody,
                    borderTopLeftRadius: 50,
                    borderBottomLeftRadius: 50,
                    overflow: 'hidden',
                    paddingInline: 16,
                  }}
                >
                  <LineClamp lineClamp={2} text={it.officeName} />
                </td>
                <td data-cy="payment-date-column" style={Styles.tableBody}>
                  <Column>
                    <span>{moment(it.transactionDate).format('ddd, MMM, DD, YYYY')}</span>
                    <span>{moment(it.transactionDate).format('hh:mm')}</span>
                  </Column>
                </td>
                <td
                  data-cy="payment-amount-column"
                  style={{
                    ...Styles.tableBody,
                    fontWeight: 700,
                  }}
                >
                  {it.totalCharge > 0 ? `$${it.totalCharge.toFixed(2)}` : '-'}
                </td>
                <td data-cy="refund-amount-column" style={{ ...Styles.tableBody, fontWeight: 700 }}>
                  {it.totalRefund > 0 ? `$${it.totalRefund.toFixed(2)}` : '-'}
                </td>
                <td data-cy="shift-date-column" style={Styles.tableBody}>
                  {it.shiftDate}
                </td>
                <td data-cy="payment-status-column" style={Styles.tableBody}>
                  <ReceiptStatus status={it.status} />
                </td>
                <td
                  style={{
                    ...Styles.tableBody,
                    borderTopRightRadius: 50,
                    borderBottomRightRadius: 50,
                  }}
                >
                  <Center>
                    <Icon name="arrow-right" />
                  </Center>
                </td>
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
    </ScrollArea>
  );
};

const Styles: Record<string, CSSProperties> = {
  tableBody: {
    color: Colors.neutral_600,
    fontSize: 15,
    textAlign: 'center',
  },
  tableHeader: {
    borderBottom: `2px solid ${Colors.neutral_50}`,
    color: Colors.primary_500,
    fontSize: 16,
    fontWeight: 700,
    paddingBottom: 12,
    textAlign: 'center',
  },
};
